import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, map, take } from 'rxjs';
import { OrderFlowAuthService } from '../../auth/order-flow-auth.service';

/**
 * Builds a route guard that allows activation when the user holds at least one of `includeRoles`,
 * redirecting to /unauthorized otherwise.
 *
 * Replaces the per-feature `can-view-X` / `can-write-X` guard files — pass a group from
 * `ROLE_GROUPS` instead, e.g. `roleGuard(ROLE_GROUPS.brokerWrite)`.
 *
 * Waits for MSAL to settle before deciding: on a full page load this can run before the active
 * account is restored, and every role check would fail.
 */
export function roleGuard(includeRoles: readonly string[]): CanActivateFn {
  return () => {
    const authService = inject(OrderFlowAuthService);
    const broadcastService = inject(MsalBroadcastService);
    const router = inject(Router);

    return broadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      map(() => (authService.hasOneOfRoles(includeRoles) ? true : router.parseUrl('/unauthorized')))
    );
  };
}
